/**
 * Shared styled-components used by the rich SideNav section variants
 * (`SideNavTeamsSection`, `SideNavPinnedSection`).
 *
 * These are the canonical home for layout / spacing / hover behaviour of
 * "My Teams" and "Pinned" rows. Consuming apps should not redeclare them;
 * use the section components, or import these primitives if they need to
 * compose a custom section.
 */
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Link } from "react-router-dom";
import Flex from "../../Flex";
import { BREAKPOINTS } from "../constants/sideNav";

const createShouldForwardProp = (blockedProps) =>
  function shouldForwardProp(prop, defaultValidatorFn) {
    if (blockedProps.includes(prop)) {
      return false;
    }

    if (typeof defaultValidatorFn === "function") {
      return defaultValidatorFn(prop);
    }

    return true;
  };

const filterIsExpandedProp = createShouldForwardProp(["isExpanded"]);

export const SectionItemList = styled("ul").withConfig({
  shouldForwardProp: filterIsExpandedProp,
})`
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: ${(props) => (props.isExpanded ? "flex-start" : "center")};
  text-align: ${(props) => (props.isExpanded ? "left" : "center")};
  display: flex;
  flex-direction: column;
  gap: ${(props) => themeGet("space.xs")(props)};

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  > li:empty {
    display: none;
  }
`;

export const SectionItem = styled("li").withConfig({
  shouldForwardProp: filterIsExpandedProp,
})`
  display: flex;
  width: 100%;
  gap: ${(props) => themeGet("space.xs")(props)};
  justify-content: ${(props) =>
    props.isExpanded ? "space-between" : "center"};
  align-items: stretch;
  padding: 1px;
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  min-height: 0;
  position: relative;

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover {
    background-color: ${(props) =>
      props.isExpanded
        ? themeGet("colors.hoverSurface")(props)
        : "transparent"};
  }

  &:hover a {
    color: ${(props) => themeGet("colors.linkText")(props)};
  }

  &.active:hover {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:empty {
    display: none;
  }

  a[data-pinned-link="true"],
  a[data-team-link="true"] {
    flex: 1;
    width: 100%;
  }
`;

export const SectionItemName = styled.span`
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  display: inline;
`;

export const SectionTeamLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xs")(props)};
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  text-decoration: none;
  color: ${(props) => themeGet("colors.bodyText")(props)};
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  flex: 1;
  width: 100%;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  > *:first-child {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }

  &:hover,
  &:focus {
    color: ${(props) => themeGet("colors.linkText")(props)};
  }

  &:focus-visible {
    outline: ${(props) => themeGet("space.xxs")(props)} solid
      ${(props) => themeGet("colors.linkText")(props)};
    outline-offset: ${(props) => themeGet("space.xxs")(props)};
  }
`;

export const SectionPinnedLink = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  text-decoration: none;
  color: ${(props) => themeGet("colors.bodyText")(props)};
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  flex: 1;
  width: 100%;
  min-height: 100%;
  align-self: stretch;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  &:hover,
  &:focus {
    color: ${(props) => themeGet("colors.linkText")(props)};
  }

  &:focus-visible {
    outline: ${(props) => themeGet("space.xxs")(props)} solid
      ${(props) => themeGet("colors.linkText")(props)};
    outline-offset: ${(props) => themeGet("space.xxs")(props)};
  }
`;

/**
 * Inner content of a pinned link (avatar + team-type bar OR placeholder +
 * name). The bar / placeholder share an identical 5px effective footprint
 * (see `TeamTypeBarPlaceholder`), so no extra per-row padding is needed
 * to align names across team and non-team rows.
 */
export const SectionPinnedLinkContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xs")(props)};
  width: 100%;
  min-height: 100%;
`;

export const CollapsedItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  cursor: pointer;
  gap: ${(props) => themeGet("space.xxs")(props)};

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover:not(.active) {
    background-color: ${(props) => themeGet("colors.hoverSurface")(props)};
  }
`;

export const CollapsedItemLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xxs")(props)};
  text-decoration: none;
  color: inherit;
`;

export const CollapsedAvatarWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const UnpinButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: ${(props) => themeGet("space.xs")(props)};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  z-index: 10;
  flex-shrink: 0;
`;

/**
 * Hides the section on small screens so the SideNav bottom-bar layout stays
 * compact. Standardised on the design-system `BREAKPOINTS.SMALL_SCREEN`
 * constant (no app-side magic numbers).
 */
export const SectionResponsiveContainer = styled(Flex)`
  @media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px) {
    display: none;
  }
`;
